import lowerCase from 'lodash.lowercase';

/**
 * References:
 * https://en.wikipedia.org/wiki/Levenshtein_distance
 * https://www.datacamp.com/community/tutorials/fuzzy-string-python#the-levenshtein-distance
 */

/**
 * Get custom ratio between two given strings.
 * 분모를 s.length와 t.length 중 큰 값으로 계산하는 ratio이다.
 * cost of a substitution을 1로 하여 계산한다.
 * lodash의 _.lowerCase를 이용하여 전처리를 한 후 일치율을 계산한다.
 *
 * @param {string} s
 * @param {string} t
 */
export function getRatio(s, t) {
  const sLowerCased = lowerCase(s);
  const tLowerCased = lowerCase(t);
  return (
    1 -
    getLevenshteinDistance(sLowerCased, tLowerCased, false) /
      Math.max(s.length, t.length)
  );
}

/**
 * Get custom ratio between two given strings.
 * 분모를 s.length와 t.length의 합으로 계산하는 ratio이다.
 * cost of a substitution을 1로 하여 계산한다.
 * lodash의 _.lowerCase를 이용하여 전처리를 한 후 일치율을 계산한다.
 *
 * @param {string} s
 * @param {string} t
 */
export function getRatio2(s, t) {
  const sLowerCased = lowerCase(s);
  const tLowerCased = lowerCase(t);
  return (
    1 -
    getLevenshteinDistance(sLowerCased, tLowerCased, false) /
      (s.length + t.length)
  );
}

/**
 * Get Levenshtein distance between two given strings
 *
 * The algorithm is from:
 * https://en.wikipedia.org/wiki/Levenshtein_distance#Iterative_with_two_matrix_rows
 *
 * @param {string} s
 * @param {string} t
 * @param {boolean} forRatio - is used for calculating Levenshtein ratio?
 */
function getLevenshteinDistance(s, t, forRatio = false) {
  const m = s.length;
  const n = t.length;

  let v0 = new Array(n + 1);
  let v1 = new Array(n + 1);

  for (let i = 0; i <= n; ++i) {
    v0[i] = i;
  }

  for (let i = 0; i < m; ++i) {
    v1[0] = i + 1;
    for (let j = 0; j < n; ++j) {
      v1[j + 1] = Math.min(
        v0[j + 1] + 1, // cost of deletions
        v1[j] + 1, // cost of insertions
        // cost of substitutions (When calculating the ratio, we set the cost of a substitution to 2.)
        v0[j] + (forRatio ? 2 : 1) * Number(s[i] !== t[j])
      );
    }
    [v0, v1] = [v1, v0];
  }

  return v0[n];
}

/**
 * Get Levenshtein ratio between two given strings
 *
 * @param {string} s
 * @param {string} t
 */
export function getLevenshteinRatio(s, t) {
  return 1 - getLevenshteinDistance(s, t, true) / (s.length + t.length);
}
